import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import { ChaptersForPathology } from "../components/ResearchCrossLinks";
import { useLanguage } from "../context/LanguageContext";
import { PATHOLOGIES } from "../lib/pathologies";
import { PATHOLOGY_RICH_CONTENT } from "../lib/pathologyContent";
import { SITE } from "../lib/constants";

const PathologyDetailPage = () => {
  const { slug } = useParams();
  const { lang } = useLanguage();

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  // Find pathology across regions
  let pathology = null;
  let region = null;
  let regionKey = null;
  for (const [key, r] of Object.entries(PATHOLOGIES)) {
    const found = r.items.find((it) => it.slug === slug);
    if (found) { pathology = found; region = r; regionKey = key; break; }
  }

  if (!pathology) {
    return (
      <main className="bg-white pt-36 pb-20 text-center">
        <div className="container-page">
          <h1 className="font-display font-black text-4xl text-navy">
            {lang === "fr" ? "Pathologie introuvable" : "Pathology not found"}
          </h1>
          <Link to="/#pathologies" className="mt-6 inline-block link-underline">
            {lang === "fr" ? "Retour aux pathologies" : "Back to pathologies"}
          </Link>
        </div>
      </main>
    );
  }

  const name = lang === "fr" ? pathology.name_fr : pathology.name_en;
  const desc = lang === "fr" ? pathology.desc_fr : pathology.desc_en;
  const regionName = lang === "fr" ? region.title_fr : region.title_en;

  // JSON-LD schema for this medical condition
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "MedicalCondition",
    "name": name,
    "description": desc,
    "associatedAnatomy": { "@type": "AnatomicalStructure", "name": regionName },
    "possibleTreatment": {
      "@type": "MedicalTherapy",
      "name": lang === "fr" ? "Chirurgie orthopédique spécialisée" : "Specialized orthopedic surgery",
      "provider": {
        "@type": "Physician",
        "name": "Dr Richard Aswad",
        "medicalSpecialty": "Orthopedic",
        "telephone": SITE.phone,
      },
    },
  };

  // Breadcrumb JSON-LD
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: lang === "fr" ? "Accueil" : "Home", item: "https://imoteps.doctor/" },
      { "@type": "ListItem", position: 2, name: lang === "fr" ? "Pathologies" : "Pathologies", item: "https://imoteps.doctor/#pathologies" },
      { "@type": "ListItem", position: 3, name: regionName, item: `https://imoteps.doctor/#pathologies` },
      { "@type": "ListItem", position: 4, name: name, item: `https://imoteps.doctor/pathologie/${slug}` },
    ],
  };

  // Rich content (5 priority pathologies enriched with H1/H2/FAQ for SEO)
  const richContent = lang === "fr" ? PATHOLOGY_RICH_CONTENT[slug] : null;

  // FAQPage JSON-LD if rich content has FAQ
  const faqJsonLd = richContent?.faq ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: richContent.faq.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  } : null;

  // Related pathologies from the same region
  const related = region.items.filter((it) => it.slug !== slug).slice(0, 4);

  // SEO meta — patient-oriented natural phrasing, local intent (Marseille)
  const seoTitle = lang === "fr"
    ? `${name} — diagnostic et chirurgie à Marseille | Dr Aswad`
    : `${name} — diagnosis & surgery in Marseille | Dr Aswad`;
  const seoDescription = lang === "fr"
    ? `${name} : symptômes, diagnostic et options chirurgicales par le Dr Richard Aswad, chirurgien orthopédique du membre supérieur à Marseille (Clinique Juge, 13008).`
    : `${name}: symptoms, diagnosis and surgical options by Dr Richard Aswad, upper-limb orthopedic surgeon in Marseille (Clinique Juge, 13008).`;

  return (
    <main data-testid="pathology-detail-page" className="bg-white pt-28 md:pt-36 pb-20">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        canonical={`/pathologie/${slug}`}
        keywords={lang === "fr"
          ? `${pathology.name_fr}, ${pathology.name_fr} Marseille, chirurgie ${regionName.toLowerCase()} Marseille, Dr Aswad, IMOTEpS`
          : `${pathology.name_en}, ${pathology.name_en} Marseille, ${regionName} surgery Marseille, Dr Aswad, IMOTEpS`}
        lang={lang}
        type="article"
        jsonLd={jsonLd}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />
      {faqJsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      )}

      <article className="container-page max-w-4xl">
        <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
          <Link to="/" className="hover:text-[#FF5A1F]">Accueil</Link>
          <span className="mx-2">/</span>
          <Link to="/#pathologies" className="hover:text-[#FF5A1F]">Pathologies</Link>
          <span className="mx-2">/</span>
          <span className="text-[#FF5A1F] font-semibold">{regionName}</span>
          <span className="mx-2">/</span>
          <span className="text-navy font-semibold">{name}</span>
        </nav>

        <header className="mt-6">
          <span className="chip">{regionName}</span>
          <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl text-navy leading-[1.05]">
            {richContent?.h1_seo || name}
          </h1>
          <p className="mt-5 text-lg text-foreground/80 leading-relaxed">
            {richContent?.intro || desc}
          </p>
        </header>

        {/* Rich SEO content (only for prioritized pathologies) */}
        {richContent && (
          <div className="mt-12 space-y-10">
            {richContent.sections.map((sec, idx) => (
              <section key={idx}>
                <h2 className="font-display text-2xl font-bold text-navy">{sec.h2}</h2>
                {sec.body && (
                  <p className="mt-3 text-base text-foreground/80 leading-relaxed">{sec.body}</p>
                )}
                {sec.list && (
                  <ul className="mt-4 space-y-2">
                    {sec.list.map((li, i) => (
                      <li key={i} className="flex gap-3 text-base text-foreground/80">
                        <i className="fa-solid fa-check text-[#FF5A1F] mt-1.5" aria-hidden="true"></i>
                        <span>{li}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}

            {/* FAQ section — eligible for Google rich snippets */}
            {richContent.faq && richContent.faq.length > 0 && (
              <section data-testid="pathology-faq">
                <h2 className="font-display text-2xl font-bold text-navy">
                  {lang === "fr" ? "Questions fréquentes — patients" : "Frequently asked questions"}
                </h2>
                <div className="mt-5 space-y-3">
                  {richContent.faq.map((item, i) => (
                    <details
                      key={i}
                      className="group rounded-xl border border-border bg-white p-5 hover:border-[#FF5A1F]/30 transition-colors"
                      data-testid={`pathology-faq-${i}`}
                    >
                      <summary className="cursor-pointer font-display font-bold text-navy text-base flex items-start justify-between gap-4 list-none">
                        <span>{item.q}</span>
                        <i className="fa-solid fa-plus text-[#FF5A1F] mt-1 group-open:hidden" aria-hidden="true"></i>
                        <i className="fa-solid fa-minus text-[#FF5A1F] mt-1 hidden group-open:inline" aria-hidden="true"></i>
                      </summary>
                      <p className="mt-3 text-sm text-foreground/80 leading-relaxed">{item.a}</p>
                    </details>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        {/* Standard sections (always shown — for non-enriched pathologies) */}
        {!richContent && (
        <div className="mt-12 space-y-10">
          <section>
            <h2 className="font-display text-2xl font-bold text-navy">
              {lang === "fr" ? "Symptômes" : "Symptoms"}
            </h2>
            <p className="mt-3 text-base text-foreground/80 leading-relaxed">
              {lang === "fr"
                ? `Les patients présentant ${name.toLowerCase()} rapportent typiquement une douleur localisée au niveau ${regionName.toLowerCase()}, une perte de mobilité ou de force, parfois associée à des sensations nocturnes ou à l'effort. Un examen clinique précis, complété par une imagerie ciblée (IRM, échographie ou arthro-scanner selon les cas), permet de poser le diagnostic.`
                : `Patients presenting ${name.toLowerCase()} typically report localized pain in the ${regionName.toLowerCase()} area, loss of mobility or strength, sometimes associated with nocturnal or exertional sensations. A precise clinical examination, supplemented by targeted imaging (MRI, ultrasound or arthro-CT depending on the case), allows the diagnosis to be made.`}
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-navy">
              {lang === "fr" ? "Traitement" : "Treatment"}
            </h2>
            <p className="mt-3 text-base text-foreground/80 leading-relaxed">
              {lang === "fr"
                ? `La prise en charge du Dr Aswad est toujours personnalisée. Un premier volet médical (repos, anti-inflammatoires, kinésithérapie, infiltrations ciblées) est systématiquement proposé lorsque la pathologie le permet. En cas d'échec ou d'indication chirurgicale directe, le Dr Aswad privilégie les techniques mini-invasives (arthroscopie, microchirurgie) pour minimiser les cicatrices, préserver les tissus sains et accélérer la récupération.`
                : `Dr Aswad's approach is always personalized. A first medical phase (rest, anti-inflammatories, physiotherapy, targeted injections) is systematically proposed when the condition allows. In case of failure or direct surgical indication, Dr Aswad favors minimally-invasive techniques (arthroscopy, microsurgery) to minimize scars, preserve healthy tissues and accelerate recovery.`}
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-navy">
              {lang === "fr" ? "Récupération et reprise d'activité" : "Recovery and return to activity"}
            </h2>
            <p className="mt-3 text-base text-foreground/80 leading-relaxed">
              {lang === "fr"
                ? `La récupération suit un protocole structuré (RAAC — Récupération Améliorée Après Chirurgie) associant rééducation progressive, suivi kinésithérapique et reprise sportive encadrée. Le Dr Aswad travaille avec un réseau de kinésithérapeutes et de préparateurs physiques partenaires pour garantir un retour optimal aux activités quotidiennes et sportives.`
                : `Recovery follows a structured protocol (ERAS — Enhanced Recovery After Surgery) combining progressive rehabilitation, physiotherapy follow-up and supervised sports return. Dr Aswad works with a network of partner physiotherapists and strength coaches to ensure optimal return to daily and sports activities.`}
            </p>
            <Link to="/#raac" className="mt-4 inline-flex link-underline">
              {lang === "fr" ? "Voir le protocole RAAC" : "See RERAS protocol"}
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
            </Link>
          </section>

          <section className="rounded-2xl bg-[#0A192F] text-white p-7 md:p-10">
            <h2 className="font-display text-2xl font-bold">
              {lang === "fr" ? "Prendre rendez-vous pour cette pathologie" : "Book for this condition"}
            </h2>
            <p className="mt-3 text-sm text-white/75">
              {lang === "fr"
                ? "Le Dr Aswad vous reçoit à la Clinique Juge (Marseille 8e). Consultation initiale, bilan d'imagerie, explication des options."
                : "Dr Aswad sees patients at Clinique Juge (Marseille 8th). Initial consultation, imaging review, options explained."}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/#contact" className="btn-primary">
                <i className="fa-solid fa-envelope-open-text" aria-hidden="true"></i>
                {lang === "fr" ? "Formulaire de contact" : "Contact form"}
              </Link>
              <a href={SITE.phoneHref} className="btn-ghost-light">
                <i className="fa-solid fa-phone" aria-hidden="true"></i>
                {SITE.phone}
              </a>
            </div>
          </section>
        </div>
        )}

        {/* Booking CTA — also shown when rich content is rendered */}
        {richContent && (
          <section className="mt-12 rounded-2xl bg-[#0A192F] text-white p-7 md:p-10">
            <h2 className="font-display text-2xl font-bold">
              {lang === "fr" ? "Prendre rendez-vous pour cette pathologie" : "Book for this condition"}
            </h2>
            <p className="mt-3 text-sm text-white/75">
              {lang === "fr"
                ? "Le Dr Aswad vous reçoit à la Clinique Juge (Marseille 8e). Consultation initiale, bilan d'imagerie, explication des options."
                : "Dr Aswad sees patients at Clinique Juge (Marseille 8th). Initial consultation, imaging review, options explained."}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/#contact" className="btn-primary">
                <i className="fa-solid fa-envelope-open-text" aria-hidden="true"></i>
                {lang === "fr" ? "Formulaire de contact" : "Contact form"}
              </Link>
              <a href={SITE.phoneHref} className="btn-ghost-light">
                <i className="fa-solid fa-phone" aria-hidden="true"></i>
                {SITE.phone}
              </a>
            </div>
          </section>
        )}

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-16">
            <h2 className="font-display text-2xl font-bold text-navy mb-6">
              {lang === "fr" ? "Autres pathologies de la région" : "Other pathologies in this region"}
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/pathologie/${r.slug}`}
                  className="group rounded-xl border border-border bg-white p-5 hover:border-[#FF5A1F]/40 hover:shadow-md transition-all"
                >
                  <p className="font-display font-bold text-navy group-hover:text-[#FF5A1F] transition-colors">
                    {lang === "fr" ? r.name_fr : r.name_en}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                    {lang === "fr" ? r.desc_fr : r.desc_en}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Academic chapters cross-link */}
        <ChaptersForPathology pathologySlug={slug} lang={lang} />
      </article>
    </main>
  );
};

export default PathologyDetailPage;
